export const DESIGN_CATALOG_ROUTES = {
  designing: "/designing",
  printing: "/printing",
};

export const createDesigningSlug = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .trim();

const normalizeDesignDiscount = (discount) =>
  typeof discount === "string" ? discount.toLowerCase() : discount;

const normalizeDesignUnit = (unit) =>
  typeof unit === "string" && unit.trim().length ? unit.trim() : "page";

const getMediaUrl = (media) =>
  media?.cloudinary?.secure_url ||
  media?.url ||
  media?.thumbnailURL ||
  (typeof media === "string" ? media : null) ||
  null;

const normalizeNumber = (value, fallback = null) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizePositiveInteger = (value, fallback = 1) => {
  const numericValue = Math.floor(Number(value));
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
};

const normalizeCustomDiscount = (customDiscount) => {
  if (!customDiscount || typeof customDiscount !== "object") return customDiscount;

  const discount =
    customDiscount.discount ?? customDiscount.customDiscount ?? null;
  const text = customDiscount.text ?? customDiscount.customDiscountText ?? null;

  return {
    ...customDiscount,
    discount,
    text,
  };
};

const normalizeDesignOptions = (options, fallbackUnit) => {
  if (!Array.isArray(options)) return [];

  return options
    .filter(Boolean)
    .map((option) => ({
      ...option,
      is_default: Boolean(option?.is_default),
      unit: normalizeDesignUnit(option?.unit || fallbackUnit),
      price: normalizeNumber(option?.price, 0),
      min_quantity: normalizeNumber(option?.min_quantity, null),
      max_quantity: normalizeNumber(option?.max_quantity, null),
      quantity_step: normalizePositiveInteger(option?.quantity_step, 1),
    }));
};

const normalizeQuantityPricingRules = (rules) => {
  if (!Array.isArray(rules)) return [];

  return [...rules]
    .filter(Boolean)
    .map((rule) => ({
      ...rule,
      is_default: Boolean(rule?.is_default),
      min_quantity: normalizeNumber(rule?.min_quantity, 1),
      max_quantity: normalizeNumber(rule?.max_quantity, null),
      rate: normalizeNumber(rule?.rate, null),
      discount_percentage: normalizeNumber(rule?.discount_percentage, 0),
    }))
    .sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0));
};

const normalizeHeroMedia = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter(Boolean)
    .map((item) => {
      const imageUrl = getMediaUrl(item?.image);
      const mediaType =
        item?.media_type ||
        item?.blockType ||
        (item?.youtube ? "youtube" : "image");

      return {
        ...item,
        media_type: mediaType,
        youtube: item?.youtube || null,
        image: imageUrl
          ? {
              ...(typeof item?.image === "object" && item.image ? item.image : {}),
              url: imageUrl,
              no_prefix: true,
            }
          : null,
      };
    })
    .filter(
      (item) =>
        (item.media_type === "youtube" && item.youtube) ||
        (item.media_type !== "youtube" && item.image?.url),
    );
};

export const normalizeDesigning = (item) => {
  if (!item) return item;

  const imageUrl = getMediaUrl(item?.image) || item?.thumbnail || null;
  const heroMediaSlides = normalizeHeroMedia(
    Array.isArray(item?.hero_media)
      ? item.hero_media
      : item?.hero_media?.slides,
  );
  const subDescription =
    item?.sub_description ??
    item?.short_description ??
    item?.shortDescription ??
    item?.subDescription ??
    null;

  return {
    ...item,
    discount: normalizeDesignDiscount(item?.discount),
    unit: normalizeDesignUnit(item?.unit),
    customDiscount: normalizeCustomDiscount(item?.customDiscount),
    pricing_mode: item?.pricing_mode || "direct",
    quantity_pricing_mode: item?.quantity_pricing_mode || "none",
    quantity_step: normalizePositiveInteger(item?.quantity_step, 1),
    design_options: normalizeDesignOptions(item?.design_options, item?.unit),
    quantity_pricing_rules: normalizeQuantityPricingRules(
      item?.quantity_pricing_rules,
    ),
    hero_media: {
      ...(item?.hero_media && typeof item.hero_media === "object" && !Array.isArray(item.hero_media)
        ? item.hero_media
        : {}),
      slides: heroMediaSlides,
    },
    sub_description: subDescription,
    slug:
      (item?.slug ? createDesigningSlug(item.slug) : undefined) ||
      (item?.title ? createDesigningSlug(item.title) : undefined),
    image: imageUrl
      ? {
          ...(typeof item?.image === "object" && item.image ? item.image : {}),
          url: imageUrl,
          no_prefix: true,
        }
      : item?.image,
  };
};

export const getDesigningIdentifier = (item) =>
  (item?.slug ? createDesigningSlug(item.slug) : "") ||
  (item?.title ? createDesigningSlug(item.title) : "") ||
  item?.id ||
  item?._id ||
  "";

export const isDesignCatalogCategory = (category) =>
  Object.prototype.hasOwnProperty.call(DESIGN_CATALOG_ROUTES, category);

export const getDesignCatalogPath = (item, category = "designing") =>
  `${DESIGN_CATALOG_ROUTES[category] || DESIGN_CATALOG_ROUTES.designing}/${getDesigningIdentifier(item)}`;

export const getDesigningPath = (item) =>
  getDesignCatalogPath(item, "designing");

export const getDesigningDisplayPrice = (item) => {
  if (!item) return 0;

  if (item?.pricing_mode === "options" && Array.isArray(item?.design_options)) {
    const optionPrices = item.design_options
      .map((option) => normalizeNumber(option?.price, null))
      .filter((price) => Number.isFinite(price) && price >= 0);

    if (optionPrices.length > 0) {
      return Math.min(...optionPrices);
    }
  }

  return normalizeNumber(item?.price, 0) || 0;
};
